import { OrderStatus, RouteType } from '@websky/graphql';
import { DataLayerEvent } from './DataLayerAnalytics/types';
import ym from 'react-yandex-metrika';

export const DATA_LAYER_KEY = 'dataLayer';

export enum MotivationGoal {
	// Seats
	ConfirmProposedSeat = 'motivation-proposedSeat-agree',
	RefuseProposedSeat = 'motivation-proposedSeat-disagree',

	ConfirmToChooseSeat = 'motivation-chooseSeat-agree',
	RefuseToChooseSeat = 'motivation-chooseSeat-disagree',

	// Baggage
	// ConfirmProposedBaggage = 'motivation-proposedBaggage-agree',
	// RefuseProposedBaggage = 'motivation-proposedBaggage-disagree',

	// Meal
	ConfirmProposedMeal = 'motivation-proposedMeal-agree',
	RefuseProposedMeal = 'motivation-proposedMeal-disagree'
}

export enum OrderGoal {
	Book = 'order-book',
	BookingError = 'order-booking-error',
	Booked = 'order-booked',
	Paid = 'order-paid',
	PaymentFailed = 'order-payment-failed',
	ItineraryReceiptDownload = 'order-itinerary-receipt-download'
}

export enum UserGoal {
	Login = 'user-login',
	Registration = 'user-registration',
	ContinueOnDesktop = 'continue-on-desktop',
	BindOrder = 'bind-order'
}

export enum CheckinGoal {
	Create = 'create-checkin',
	Start = 'start-checkin',
	CompleteWithoutPayment = 'checkin-complete-without-payment',
	CompleteWithPayment = 'checkin-complete-with-payment',
	Paid = 'checkin-paid',
	PaymentCancelled = 'checkin-payment-cancelled',
	Cancelled = 'checkin-cancelled',
	AddTraveller = 'checkin-add-traveller',
	RemoveTraveller = 'checkin-remove-traveller',
	ResendTicket = 'checkin-resend-ticket',
	AppleWalletPassDownload = 'checkin-apple-wallet-pass-download',
	BoardingPassDownload = 'checkin-boarding-pass-download'
}

export enum SplitTestGoal {
	RefusedToAuthorize = 'test-refused-to-authorize',
	AgreedToAuthorize = 'test-agreed-to-authorize'
}

export enum InsuranceGoal {
	InsurancePopupYes = 'insurance_popup_yes',
	InsurancePopupNo = 'insurance_popup_no',
	PurchaseWithInsurance = 'purchase_with_insurance_'
}

export enum SplitTest {
	// AskUserToAuthorize = 'ask-user-to-authorize', // complete
	OneButtonBaggageSelector = 'one-button-baggage-selector',
	SkipServicesSteps = 'skip-services-steps',
	SkipServicesRecommendations = 'skip-services-recommendations'
}

export enum SearchGoal {
	SuccessfulSearchWithHotels = 'successful_search_with_hotels',
	SuccessfulSearchWithoutHotels = 'successful_search_without_hotels'
}

export enum PurchaseGoal {
	PurchaseWithExtraServices = 'purchase_with_extra_services'
}

export enum InfoMessageGoal {
	PurchaseTransferShow = 'purchase_transfer_show',
	PurchaseTransferClick = 'purchase_transfer_click'
}

export type Goal =
	| MotivationGoal
	| OrderGoal
	| CheckinGoal
	| UserGoal
	| SplitTestGoal
	| InsuranceGoal
	| SearchGoal
	| PurchaseGoal
	| InfoMessageGoal
	| DataLayerEvent;

export enum ActionType {
	Detail = 'detail',
	Add = 'add',
	Remove = 'remove',
	Purchase = 'purchase'
}

export enum ProductType {
	FareFamily = 'Fare Family',
	Order = 'Order',
	Insurance = 'Insurance',
	Transfer = 'Transfer',
	Baggage = 'Baggage',
	Meal = 'Meal',
	Seat = 'Seat'
}

export enum TransportLegType {
	Direct = 'direct',
	Transfer = 'transfer'
}

export interface Product {
	id: string | number;
	category: ProductType;
	name?: string;
	brand?: string;
	coupon?: string;
	position?: number;
	price?: number;
	quantity?: number;
	variant?: string;
}

export interface ActionField {
	id: string;
	coupon?: string;
	goal_id?: number;
	revenue?: number;
}

export type FunnelParametersType = {
	airport_from?: string;
	airport_to?: string;
	date_start?: string;
	date_end?: string;
	passenger_type?: string[];
	route_type?: RouteType;
	flight_number?: string;
	avia_operator?: string;
	transport_leg?: TransportLegType;
	payment_type?: string;
	auth_type?: string;
	register_type?: string;
	search_id?: string;
};

export const reachGoal = (goal: Goal, parameters?: any) => {
	if (process.env.NODE_ENV === 'production') {
		try {
			window?.top100Counter?.trackEvent(goal);
			ym('reachGoal', goal, parameters);
		} catch (e) {
			console.warn('YM reachGoal failed');
		}
	}
};

export const setSplitTest = (testName: SplitTest, value: any) => {
	if (process.env.NODE_ENV === 'production') {
		try {
			ym('params', {
				[`test-${testName}`]: value
			});
		} catch (e) {
			console.warn('YM is not initialized');
		}
	}
};

// Pixel
export enum PixelEvent {
	PageView = 'page_view',
	Search = 'search',
	AuthDetected = 'auth_detected',
	Click = 'click',
	OrderIntent = 'order_intent'
}

type EventDescrMap = {
	[PixelEvent.PageView]: {
		page?: 'main' | 'results' | 'checkin';
	};
	[PixelEvent.Search]: {
		from?: string;
		to?: string;
		date_depart?: string;
		date_return?: string;
		adt?: number;
		cld?: number;
		inf?: number;
		inf_with_seat?: number;
		scf?: number;
		scm?: number;
		class?: string;
		hotels?: boolean;
		miles?: boolean;
		promocode?: boolean;
	};
	[PixelEvent.AuthDetected]: {
		auth_type: string;
	};
	[PixelEvent.Click]: {
		button_id: string;
	};
	[PixelEvent.OrderIntent]: {
		status: OrderStatus;
	};
};

export type PixelMessage<T extends PixelEvent = PixelEvent> = {
	cdp_pixel_id?: string;
	timestamp: string;
	eventName: T;
	url: string;
	referrer: string;
	user_agent: string;
	fingerprint?: string;
	metadata: Object;
	event_descr: EventDescrMap[T];
};

export interface PixelResponse {
	data: any;
	cdpId: string;
}
